<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import BillingAutomationService from "@/services/BillingAutomationService";
import CustomerService from "@/services/CustomerService";
import FileTemplateService from "@/services/FileTemplateService";
import { useUserStore } from "@/stores/userStore";
import { useEntitlementStore } from "@/stores/entitlementStore";

const disableAuthForE2E = process.env.VUE_APP_DISABLE_AUTH === "true";
const userStore = useUserStore();
const entitlementStore = useEntitlementStore();
const hasEInvoice = computed(() => disableAuthForE2E || entitlementStore.hasModule("e-invoice"));
const canManage = computed(() => disableAuthForE2E || userStore.hasPermission("billing.manage"));
const canApprove = computed(() => disableAuthForE2E || userStore.hasPermission("billing.approve"));
const canDispatch = computed(() => disableAuthForE2E || userStore.hasPermission("billing.dispatch"));

const tabs = [
  { id: "run", label: "Monatslauf", icon: "bi-calendar3" },
  { id: "orders", label: "Aufträge", icon: "bi-receipt" },
  { id: "assignments", label: "Quellzuordnungen", icon: "bi-diagram-3" },
];
const activeTab = ref("run");
const loading = ref(true);
const busyAction = ref("");
const loadError = ref("");
const operationError = ref("");
const operationNotice = ref("");
const orders = ref([]);
const assignments = ref([]);
const customers = ref([]);
const templates = ref([]);
const workflow = ref(null);
const approvalInputs = reactive({});
const cancellationTarget = ref(null);
const cancellationReason = ref("");
const cancellationInvoiceId = ref("");
const requestDatevTransfer = ref(false);

function localDate(year, month, day = 1) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function localMonth(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

const now = new Date();
const priorMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
const currentYear = now.getFullYear();
const previewForm = reactive({
  billingRunId: "",
  billingMonth: localMonth(priorMonth.getFullYear(), priorMonth.getMonth() + 1),
  timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin",
  sourceSystem: "deterministic-kimai",
});
const runLookupId = ref("");
const orderForm = reactive({
  idempotencyKey: "",
  customerId: "",
  orderNumber: "",
  validFrom: localDate(now.getFullYear(), now.getMonth() + 1),
  validUntil: "",
  currencyCode: "EUR",
  hourlyRate: "0.00",
  monthlyBillableHoursCap: "",
  monthlyFlatFee: "0.00",
  monthlyNetAmountCap: "",
});
const assignmentForm = reactive({
  idempotencyKey: "",
  sourceSystem: "deterministic-kimai",
  customerReference: "",
  projectReference: "",
  orderId: "",
});
const documentForm = reactive({
  invoiceDate: localDate(now.getFullYear(), now.getMonth() + 1, now.getDate()),
  sequenceKey: `invoice-${currentYear}`,
  invoiceNumberPrefix: `${currentYear}-`,
  templateFileId: "",
  paymentTerms: "Zahlbar innerhalb von 14 Tagen ohne Abzug.",
  taxRate: "19.00",
  zugferdVersionId: "230",
  zugferdProfileId: "32",
});

const runOrders = computed(() => workflow.value?.run?.orders || []);
const sourceEntries = computed(() => workflow.value?.run?.sourceEntries || []);
const workflowByOrder = computed(() => new Map(
  (workflow.value?.orders || []).map((item) => [item.orderId, item])
));
const pendingFeedbackCount = computed(() => (workflow.value?.orders || [])
  .filter((item) => ["Pending", "Failed"].includes(item.feedbackStatus)).length);

function newKey(prefix) {
  const value = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${value}`;
}

function customerName(customerId) {
  const customer = customers.value.find((item) => item.id === customerId);
  return customer?.name || customer?.companyName || customerId;
}

function orderName(orderId) {
  return orders.value.find((item) => item.id === orderId)?.orderNumber || orderId;
}

function formatMoney(value, currencyCode = "EUR") {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currencyCode || "EUR",
  }).format(Number(value || 0));
}

function formatHours(value) {
  return `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(Number(value || 0))} h`;
}

function invoiceStatusLabel(status) {
  return ({ Draft: "Entwurf", Finalized: "Finalisiert", Sent: "Versendet", Cancelled: "Storniert" })[status] || "Offen";
}

function invoiceStatusClass(status) {
  return ({
    Draft: "text-bg-secondary",
    Finalized: "text-bg-primary",
    Sent: "text-bg-success",
    Cancelled: "text-bg-danger",
  })[status] || "text-bg-light border";
}

function feedbackStatusLabel(status) {
  return ({ Pending: "Ausstehend", Failed: "Fehlgeschlagen", Succeeded: "Bestätigt" })[status] || "Nicht eingereiht";
}

function feedbackStatusClass(status) {
  return ({
    Pending: "text-bg-warning",
    Failed: "text-bg-danger",
    Succeeded: "text-bg-success",
  })[status] || "text-bg-light border";
}

function errorMessage(error, fallback) {
  return error?.response?.data?.detail
    || error?.response?.data?.title
    || error?.message
    || fallback;
}

function asOptionalNumber(value) {
  return value === "" || value === null || value === undefined ? null : Number(value);
}

function setWorkflow(value) {
  workflow.value = value;
  if (value?.run?.id) {
    runLookupId.value = value.run.id;
    previewForm.billingRunId = value.run.id;
  }
  for (const order of value?.run?.orders || []) {
    if (!approvalInputs[order.orderId]) {
      approvalInputs[order.orderId] = {
        hoursCapAccepted: false,
        amountCapAccepted: false,
        flatFeeAccepted: false,
      };
    }
  }
}

function beginAction(key) {
  busyAction.value = key;
  operationError.value = "";
  operationNotice.value = "";
}

function completeAction(message) {
  operationNotice.value = message;
  busyAction.value = "";
}

function failAction(error, fallback) {
  operationError.value = errorMessage(error, fallback);
  busyAction.value = "";
}

async function loadReferenceData() {
  loading.value = true;
  loadError.value = "";
  const [ordersResult, assignmentsResult, customersResult, templatesResult] = await Promise.allSettled([
    BillingAutomationService.getOrders(),
    BillingAutomationService.getSourceAssignments(previewForm.sourceSystem),
    CustomerService.getAll(null, 1, 200),
    FileTemplateService.getInvoiceTemplates(FileTemplateService.OPENOFFICE_FILE_TYPES),
  ]);

  if (ordersResult.status === "rejected" || assignmentsResult.status === "rejected") {
    loadError.value = errorMessage(
      ordersResult.status === "rejected" ? ordersResult.reason : assignmentsResult.reason,
      "Die Abrechnungsdaten konnten nicht geladen werden."
    );
  } else {
    orders.value = Array.isArray(ordersResult.value?.data) ? ordersResult.value.data : [];
    assignments.value = Array.isArray(assignmentsResult.value?.data) ? assignmentsResult.value.data : [];
  }
  customers.value = customersResult.status === "fulfilled" && Array.isArray(customersResult.value?.data)
    ? customersResult.value.data : [];
  templates.value = templatesResult.status === "fulfilled" && Array.isArray(templatesResult.value?.data)
    ? templatesResult.value.data : [];
  if (!documentForm.templateFileId && templates.value.length) {
    documentForm.templateFileId = templates.value[0].id;
  }
  loading.value = false;
}

async function refreshAssignments() {
  try {
    const response = await BillingAutomationService.getSourceAssignments(previewForm.sourceSystem);
    assignments.value = Array.isArray(response?.data) ? response.data : [];
    assignmentForm.sourceSystem = previewForm.sourceSystem;
  } catch (error) {
    operationError.value = errorMessage(error, "Die Quellzuordnungen konnten nicht geladen werden.");
  }
}

function resetPreviewIdentity() {
  previewForm.billingRunId = "";
  workflow.value = null;
}

async function changeSourceSystem() {
  resetPreviewIdentity();
  await refreshAssignments();
}

async function createOrder() {
  beginAction("create-order");
  orderForm.idempotencyKey ||= newKey("ui-order");
  try {
    await BillingAutomationService.createOrder({
      ...orderForm,
      validUntil: orderForm.validUntil || null,
      hourlyRate: Number(orderForm.hourlyRate),
      monthlyBillableHoursCap: asOptionalNumber(orderForm.monthlyBillableHoursCap),
      monthlyFlatFee: Number(orderForm.monthlyFlatFee),
      monthlyNetAmountCap: asOptionalNumber(orderForm.monthlyNetAmountCap),
    });
    orderForm.idempotencyKey = "";
    orderForm.orderNumber = "";
    const response = await BillingAutomationService.getOrders();
    orders.value = response.data || [];
    completeAction("Auftrag wurde unveränderlich angelegt.");
  } catch (error) {
    failAction(error, "Der Auftrag konnte nicht angelegt werden.");
  }
}

async function createAssignment() {
  beginAction("create-assignment");
  assignmentForm.idempotencyKey ||= newKey("ui-assignment");
  try {
    await BillingAutomationService.createSourceAssignment(assignmentForm);
    assignmentForm.idempotencyKey = "";
    assignmentForm.customerReference = "";
    assignmentForm.projectReference = "";
    previewForm.sourceSystem = assignmentForm.sourceSystem;
    resetPreviewIdentity();
    await refreshAssignments();
    completeAction("Quellzuordnung wurde tenantgebunden angelegt.");
  } catch (error) {
    failAction(error, "Die Quellzuordnung konnte nicht angelegt werden.");
  }
}

async function createPreview() {
  beginAction("preview");
  previewForm.billingRunId ||= window.crypto.randomUUID();
  try {
    const response = await BillingAutomationService.createPreview({
      ...previewForm,
      billingMonth: `${previewForm.billingMonth}-01`,
    });
    setWorkflow(response.data);
    completeAction("Monatsvorschau wurde als unveränderlicher Snapshot gespeichert.");
  } catch (error) {
    failAction(error, "Die Monatsvorschau konnte nicht erstellt werden.");
  }
}

async function loadRun() {
  if (!runLookupId.value) return;
  beginAction("load-run");
  try {
    const response = await BillingAutomationService.getRun(runLookupId.value.trim());
    setWorkflow(response.data);
    completeAction("Billing Run wurde geladen.");
  } catch (error) {
    failAction(error, "Der Billing Run konnte nicht geladen werden.");
  }
}

function approvalReady(order) {
  const input = approvalInputs[order.orderId] || {};
  return (!order.requiresHoursCapAcceptance || input.hoursCapAccepted)
    && (!order.requiresAmountCapAcceptance || input.amountCapAccepted)
    && (!order.requiresFlatFeeAcceptance || input.flatFeeAccepted);
}

async function approveOrder(order) {
  const key = `approve-${order.orderId}`;
  beginAction(key);
  try {
    const response = await BillingAutomationService.approve(workflow.value.run.id, order.orderId, {
      idempotencyKey: `ui-approve-${workflow.value.run.id}-${order.orderId}`,
      ...(approvalInputs[order.orderId] || {}),
    });
    setWorkflow(response.data);
    completeAction(`${order.orderNumber} wurde freigegeben.`);
  } catch (error) {
    failAction(error, "Die Freigabe konnte nicht gespeichert werden.");
  }
}

async function generateDocuments(order) {
  const key = `documents-${order.orderId}`;
  beginAction(key);
  try {
    const response = await BillingAutomationService.generateDocuments(
      workflow.value.run.id,
      order.orderId,
      {
        idempotencyKey: `ui-documents-${workflow.value.run.id}-${order.orderId}`,
        invoiceDate: documentForm.invoiceDate,
        sequenceKey: documentForm.sequenceKey.trim(),
        invoiceNumberPrefix: documentForm.invoiceNumberPrefix.trim(),
        templateFileId: documentForm.templateFileId,
        paymentTerms: documentForm.paymentTerms.trim(),
        taxRate: Number(documentForm.taxRate),
        zugferdVersionId: documentForm.zugferdVersionId,
        zugferdProfileId: documentForm.zugferdProfileId,
      }
    );
    setWorkflow(response.data);
    completeAction(`Rechnung und Stundenzettel für ${order.orderNumber} wurden erzeugt.`);
  } catch (error) {
    failAction(error, "Die Dokumente konnten nicht erzeugt werden.");
  }
}

async function finalizeOrder(order) {
  const key = `finalize-${order.orderId}`;
  beginAction(key);
  try {
    const response = await BillingAutomationService.finalize(
      workflow.value.run.id,
      order.orderId,
      { idempotencyKey: `ui-finalize-${workflow.value.run.id}-${order.orderId}` }
    );
    setWorkflow(response.data);
    completeAction(`${order.orderNumber} wurde finalisiert.`);
  } catch (error) {
    failAction(error, "Die Rechnung konnte nicht finalisiert werden.");
  }
}

async function markSent(order) {
  const key = `sent-${order.orderId}`;
  beginAction(key);
  try {
    const response = await BillingAutomationService.markSent(
      workflow.value.run.id,
      order.orderId,
      {
        idempotencyKey: `ui-sent-${workflow.value.run.id}-${order.orderId}`,
        requestDatevTransfer: requestDatevTransfer.value,
      }
    );
    setWorkflow(response.data.workflow);
    const details = [];
    if (response.data.sourceFeedbackError) details.push(`Quellrückmeldung: ${response.data.sourceFeedbackError}`);
    if (response.data.datevError) details.push(`DATEV: ${response.data.datevError}`);
    completeAction(details.length
      ? `${order.orderNumber} ist als versendet gespeichert. ${details.join(" ")}`
      : `${order.orderNumber} ist als versendet gespeichert; Rückmeldungen wurden eingereiht.`);
  } catch (error) {
    failAction(error, "Der Versandstatus konnte nicht gespeichert werden.");
  }
}

function openCancellation(order) {
  cancellationTarget.value = order;
  cancellationReason.value = "";
  cancellationInvoiceId.value = "";
  operationError.value = "";
}

async function cancelOrder() {
  const order = cancellationTarget.value;
  if (!order) return;
  beginAction(`cancel-${order.orderId}`);
  try {
    const response = await BillingAutomationService.cancel(
      workflow.value.run.id,
      order.orderId,
      {
        idempotencyKey: `ui-cancel-${workflow.value.run.id}-${order.orderId}`,
        reason: cancellationReason.value.trim(),
        cancellationInvoiceId: cancellationInvoiceId.value.trim() || null,
      }
    );
    setWorkflow(response.data);
    cancellationTarget.value = null;
    completeAction(`${order.orderNumber} wurde storniert.`);
  } catch (error) {
    failAction(error, "Die Rechnung konnte nicht storniert werden.");
  }
}

async function recoverDispatch() {
  beginAction("recovery");
  try {
    const response = await BillingAutomationService.recoverDispatch(workflow.value.run.id);
    setWorkflow(response.data.workflow);
    const suffix = response.data.errors?.length ? ` ${response.data.errors.join(" ")}` : "";
    completeAction(`Recovery geprüft: ${response.data.sourceFeedbackQueued} Quell- und ${response.data.datevQueued} DATEV-Einreihungen.${suffix}`);
  } catch (error) {
    failAction(error, "Die Dispatch-Recovery konnte nicht ausgeführt werden.");
  }
}

async function dispatchFeedback() {
  beginAction("feedback");
  try {
    const response = await BillingAutomationService.dispatchFeedback(workflow.value.run.id);
    setWorkflow(response.data.workflow);
    completeAction(`Quellrückmeldung: ${response.data.succeeded} erfolgreich, ${response.data.failed} fehlgeschlagen.`);
  } catch (error) {
    failAction(error, "Die Quellrückmeldung konnte nicht verarbeitet werden.");
  }
}

async function download(fileId, fallbackName) {
  try {
    await FileTemplateService.downloadFile(fileId, fallbackName);
  } catch (error) {
    operationError.value = errorMessage(error, "Der Download ist fehlgeschlagen.");
  }
}

onMounted(loadReferenceData);
</script>

<template>
  <div class="billing-page text-start">
    <header class="billing-header">
      <div>
        <h1>Kimai-Abrechnung</h1>
        <p>Monatsläufe, Freigaben und Versandstatus</p>
      </div>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm icon-command"
        :disabled="loading"
        title="Daten aktualisieren"
        aria-label="Daten aktualisieren"
        @click="loadReferenceData"
      >
        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
      </button>
    </header>

    <nav class="billing-tabs" aria-label="Abrechnungsbereiche">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        :class="{ active: activeTab === tab.id }"
        :aria-pressed="activeTab === tab.id"
        @click="activeTab = tab.id"
      >
        <i class="bi" :class="tab.icon" aria-hidden="true"></i>{{ tab.label }}
      </button>
    </nav>

    <div v-if="loadError" class="alert alert-danger" role="alert" data-testid="billing-load-error">{{ loadError }}</div>
    <div v-if="operationError" class="alert alert-danger" role="alert" data-testid="billing-operation-error">{{ operationError }}</div>
    <div v-if="operationNotice" class="alert alert-success" role="status" data-testid="billing-operation-notice">{{ operationNotice }}</div>
    <div v-if="loading" class="billing-loading" role="status">
      <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Abrechnungsdaten werden geladen
    </div>

    <template v-else>
      <section v-if="activeTab === 'orders'" aria-labelledby="orders-heading">
        <div class="section-heading">
          <h2 id="orders-heading">Aufträge</h2><span>{{ orders.length }}</span>
        </div>
        <form v-if="canManage" class="work-form order-form" data-testid="billing-order-form" @submit.prevent="createOrder">
          <label>Kunde
            <select v-model="orderForm.customerId" class="form-select form-select-sm" required>
              <option value="" disabled>Auswählen</option>
              <option v-for="customer in customers" :key="customer.id" :value="customer.id">{{ customer.name || customer.companyName || customer.id }}</option>
            </select>
          </label>
          <label>Auftragsnummer<input v-model.trim="orderForm.orderNumber" class="form-control form-control-sm" maxlength="100" required data-testid="order-number" /></label>
          <label>Gültig ab<input v-model="orderForm.validFrom" type="date" class="form-control form-control-sm" required /></label>
          <label>Gültig bis<input v-model="orderForm.validUntil" type="date" class="form-control form-control-sm" /></label>
          <label>Stundensatz<input v-model="orderForm.hourlyRate" type="number" min="0" step="0.01" class="form-control form-control-sm" required /></label>
          <label>Stundencap<input v-model="orderForm.monthlyBillableHoursCap" type="number" min="0.01" step="0.01" class="form-control form-control-sm" placeholder="Kein Cap" /></label>
          <label>Pauschale<input v-model="orderForm.monthlyFlatFee" type="number" min="0" step="0.01" class="form-control form-control-sm" required /></label>
          <label>Betragscap<input v-model="orderForm.monthlyNetAmountCap" type="number" min="0.01" step="0.01" class="form-control form-control-sm" placeholder="Kein Cap" /></label>
          <label>Währung<input v-model.trim="orderForm.currencyCode" class="form-control form-control-sm text-uppercase" maxlength="3" required /></label>
          <button type="submit" class="btn btn-primary btn-sm align-self-end" :disabled="busyAction === 'create-order' || !customers.length">
            <span v-if="busyAction === 'create-order'" class="spinner-border spinner-border-sm me-1"></span><i v-else class="bi bi-plus-lg me-1"></i>Anlegen
          </button>
        </form>
        <div class="table-responsive">
          <table class="table table-sm align-middle billing-table">
            <thead><tr><th>Auftragsnummer</th><th>Kunde</th><th>Gültigkeit</th><th class="text-end">Stundensatz</th><th class="text-end">Stundencap</th><th class="text-end">Pauschale</th><th class="text-end">Betragscap</th></tr></thead>
            <tbody>
              <tr v-for="order in orders" :key="order.id">
                <td><strong>{{ order.orderNumber }}</strong></td><td>{{ customerName(order.customerId) }}</td>
                <td>{{ order.validFrom }} – {{ order.validUntil || 'offen' }}</td>
                <td class="text-end">{{ formatMoney(order.hourlyRate, order.currencyCode) }}</td>
                <td class="text-end">{{ order.monthlyBillableHoursCap == null ? '–' : formatHours(order.monthlyBillableHoursCap) }}</td>
                <td class="text-end">{{ formatMoney(order.monthlyFlatFee, order.currencyCode) }}</td>
                <td class="text-end">{{ order.monthlyNetAmountCap == null ? '–' : formatMoney(order.monthlyNetAmountCap, order.currencyCode) }}</td>
              </tr>
              <tr v-if="!orders.length"><td colspan="7" class="empty-row">Noch keine Aufträge vorhanden.</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-else-if="activeTab === 'assignments'" aria-labelledby="assignments-heading">
        <div class="section-heading"><h2 id="assignments-heading">Quellzuordnungen</h2><span>{{ assignments.length }}</span></div>
        <form v-if="canManage" class="work-form assignment-form" data-testid="billing-assignment-form" @submit.prevent="createAssignment">
          <label>Quellsystem<input v-model.trim="assignmentForm.sourceSystem" class="form-control form-control-sm" maxlength="50" required /></label>
          <label>Kundenreferenz<input v-model.trim="assignmentForm.customerReference" class="form-control form-control-sm" maxlength="200" required data-testid="assignment-customer" /></label>
          <label>Projektreferenz<input v-model.trim="assignmentForm.projectReference" class="form-control form-control-sm" maxlength="200" required data-testid="assignment-project" /></label>
          <label>Auftrag
            <select v-model="assignmentForm.orderId" class="form-select form-select-sm" required>
              <option value="" disabled>Auswählen</option><option v-for="order in orders" :key="order.id" :value="order.id">{{ order.orderNumber }}</option>
            </select>
          </label>
          <button type="submit" class="btn btn-primary btn-sm align-self-end" :disabled="busyAction === 'create-assignment' || !orders.length">
            <span v-if="busyAction === 'create-assignment'" class="spinner-border spinner-border-sm me-1"></span><i v-else class="bi bi-plus-lg me-1"></i>Zuordnen
          </button>
        </form>
        <div class="table-responsive">
          <table class="table table-sm align-middle billing-table">
            <thead><tr><th>Quellsystem</th><th>Kundenreferenz</th><th>Projektreferenz</th><th>Auftragsnummer</th></tr></thead>
            <tbody>
              <tr v-for="assignment in assignments" :key="assignment.id"><td>{{ assignment.sourceSystem }}</td><td>{{ assignment.customerReference }}</td><td>{{ assignment.projectReference }}</td><td><strong>{{ assignment.orderNumber || orderName(assignment.orderId) }}</strong></td></tr>
              <tr v-if="!assignments.length"><td colspan="4" class="empty-row">Keine Zuordnungen für dieses Quellsystem.</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <template v-else>
        <section class="run-toolbar" aria-labelledby="preview-heading">
          <div class="section-heading"><h2 id="preview-heading">Monatsvorschau</h2></div>
          <form class="preview-form" @submit.prevent="createPreview">
            <label>Monat<input v-model="previewForm.billingMonth" type="month" class="form-control form-control-sm" required data-testid="billing-month" @change="resetPreviewIdentity" /></label>
            <label>Zeitzone<input v-model.trim="previewForm.timeZoneId" class="form-control form-control-sm" maxlength="100" required @change="resetPreviewIdentity" /></label>
            <label>Quellsystem<input v-model.trim="previewForm.sourceSystem" class="form-control form-control-sm" maxlength="50" required @change="changeSourceSystem" /></label>
            <button type="submit" class="btn btn-primary btn-sm align-self-end" :disabled="!canManage || busyAction === 'preview'">
              <span v-if="busyAction === 'preview'" class="spinner-border spinner-border-sm me-1"></span><i v-else class="bi bi-search me-1"></i>Vorschau erstellen
            </button>
          </form>
          <form class="lookup-form" @submit.prevent="loadRun">
            <label for="billingRunId">Vorhandenen Run laden</label>
            <div class="input-group input-group-sm"><input id="billingRunId" v-model.trim="runLookupId" class="form-control" placeholder="Billing-Run-ID" /><button class="btn btn-outline-secondary" type="submit" :disabled="!runLookupId || busyAction === 'load-run'"><i class="bi bi-box-arrow-in-down me-1"></i>Laden</button></div>
          </form>
        </section>

        <template v-if="workflow">
          <div class="run-summary" data-testid="billing-run-summary">
            <div><span>Billing Run</span><strong>{{ workflow.run.id }}</strong></div>
            <div><span>Monat</span><strong>{{ workflow.run.billingMonth }}</strong></div>
            <div><span>Snapshot</span><strong :title="workflow.run.snapshotHash">{{ workflow.run.snapshotHash.slice(0, 12) }}…</strong></div>
            <div><span>Quellzeilen</span><strong>{{ sourceEntries.length }}</strong></div>
          </div>

          <section class="document-settings" aria-labelledby="documents-heading">
            <div class="section-heading"><h2 id="documents-heading">Dokumentvorgaben</h2></div>
            <div class="document-grid">
              <label>Rechnungsdatum<input v-model="documentForm.invoiceDate" type="date" class="form-control form-control-sm" required /></label>
              <label>Nummernkreis<input v-model.trim="documentForm.sequenceKey" class="form-control form-control-sm" required /></label>
              <label>Präfix<input v-model.trim="documentForm.invoiceNumberPrefix" class="form-control form-control-sm" required /></label>
              <label>Vorlage<select v-model="documentForm.templateFileId" class="form-select form-select-sm" required data-testid="billing-template"><option value="" disabled>Auswählen</option><option v-for="template in templates" :key="template.id" :value="template.id">{{ template.originalFileName || template.fileName || template.id }}</option></select></label>
              <label>Steuersatz %<input v-model="documentForm.taxRate" type="number" min="0" step="0.01" class="form-control form-control-sm" required /></label>
              <label>ZUGFeRD<select v-model="documentForm.zugferdVersionId" class="form-select form-select-sm"><option value="230">2.3</option><option value="220">2.2</option><option value="210">2.1</option></select></label>
              <label class="payment-terms">Zahlungsbedingungen<input v-model.trim="documentForm.paymentTerms" class="form-control form-control-sm" maxlength="500" required /></label>
            </div>
          </section>

          <section class="workflow-section" aria-labelledby="workflow-heading">
            <div class="section-heading workflow-title">
              <h2 id="workflow-heading">Freigabe und Lifecycle</h2>
              <div v-if="canDispatch" class="dispatch-tools">
                <label class="form-check"><input v-model="requestDatevTransfer" class="form-check-input" type="checkbox" /><span class="form-check-label">DATEV einreihen</span></label>
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="busyAction === 'recovery'" @click="recoverDispatch"><i class="bi bi-arrow-repeat me-1"></i>Recovery</button>
                <button type="button" class="btn btn-outline-primary btn-sm" :disabled="busyAction === 'feedback' || pendingFeedbackCount === 0" @click="dispatchFeedback"><i class="bi bi-send-check me-1"></i>Rückmeldungen senden</button>
              </div>
            </div>

            <article v-for="order in runOrders" :key="order.orderId" class="workflow-item" :data-testid="`billing-order-${order.orderNumber}`">
              <header>
                <div><h3>{{ order.orderNumber }}</h3><span>{{ customerName(order.customerId) }} · {{ order.projects.map(project => project.projectReference).join(', ') }}</span></div>
                <div class="status-badges">
                  <span class="badge" :class="invoiceStatusClass(workflowByOrder.get(order.orderId)?.invoiceStatus)">{{ invoiceStatusLabel(workflowByOrder.get(order.orderId)?.invoiceStatus) }}</span>
                  <span class="badge" :class="feedbackStatusClass(workflowByOrder.get(order.orderId)?.feedbackStatus)">Rückmeldung: {{ feedbackStatusLabel(workflowByOrder.get(order.orderId)?.feedbackStatus) }}</span>
                </div>
              </header>

              <dl class="calculation-grid">
                <div><dt>Quellstunden</dt><dd>{{ formatHours(order.sourceHours) }}</dd></div>
                <div><dt>Abrechenbar</dt><dd>{{ formatHours(order.billableHours) }}</dd></div>
                <div><dt>Ausgeschlossen</dt><dd :class="{ 'text-danger': order.excludedHours > 0 }">{{ formatHours(order.excludedHours) }}</dd></div>
                <div><dt>Stundenbetrag</dt><dd>{{ formatMoney(order.hourlyNetAmount, order.currencyCode) }}</dd></div>
                <div><dt>Pauschale</dt><dd>{{ formatMoney(order.flatFeeNetAmount, order.currencyCode) }}</dd></div>
                <div><dt>Cap-Reduktion</dt><dd :class="{ 'text-danger': order.capReduction > 0 }">{{ formatMoney(order.capReduction, order.currencyCode) }}</dd></div>
                <div class="net-total"><dt>Netto</dt><dd>{{ formatMoney(order.netAmount, order.currencyCode) }}</dd></div>
              </dl>

              <div v-if="!workflowByOrder.get(order.orderId)?.approvalId" class="acceptance-strip">
                <label v-if="order.requiresHoursCapAcceptance" class="form-check"><input v-model="approvalInputs[order.orderId].hoursCapAccepted" type="checkbox" class="form-check-input" :disabled="!canApprove" /><span class="form-check-label">Ausgeschlossene Stunden geprüft</span></label>
                <label v-if="order.requiresAmountCapAcceptance" class="form-check"><input v-model="approvalInputs[order.orderId].amountCapAccepted" type="checkbox" class="form-check-input" :disabled="!canApprove" /><span class="form-check-label">Betragscap geprüft</span></label>
                <label v-if="order.requiresFlatFeeAcceptance" class="form-check"><input v-model="approvalInputs[order.orderId].flatFeeAccepted" type="checkbox" class="form-check-input" :disabled="!canApprove" /><span class="form-check-label">Pauschale geprüft</span></label>
                <span v-if="!order.requiresHoursCapAcceptance && !order.requiresAmountCapAcceptance && !order.requiresFlatFeeAcceptance" class="text-muted small">Keine Cap- oder Pauschalenabweichung.</span>
              </div>

              <div v-if="workflowByOrder.get(order.orderId)?.feedbackLastError" class="feedback-error" role="status">
                <strong>Letzter Rückmeldefehler:</strong> {{ workflowByOrder.get(order.orderId).feedbackLastError }}
                <span>Versuche: {{ workflowByOrder.get(order.orderId).feedbackAttemptCount }}</span>
              </div>

              <footer class="workflow-actions">
                <div class="document-links">
                  <button v-if="workflowByOrder.get(order.orderId)?.invoicePdfFileId" type="button" class="btn btn-link btn-sm" @click="download(workflowByOrder.get(order.orderId).invoicePdfFileId, `${order.orderNumber}-Rechnung.pdf`)"><i class="bi bi-file-earmark-pdf me-1"></i>Rechnung</button>
                  <button v-if="workflowByOrder.get(order.orderId)?.eInvoiceFileId" type="button" class="btn btn-link btn-sm" @click="download(workflowByOrder.get(order.orderId).eInvoiceFileId, `${order.orderNumber}-ZUGFeRD.pdf`)"><i class="bi bi-file-earmark-check me-1"></i>E-Rechnung</button>
                  <button v-if="workflowByOrder.get(order.orderId)?.timesheetFileId" type="button" class="btn btn-link btn-sm" @click="download(workflowByOrder.get(order.orderId).timesheetFileId, `${order.orderNumber}-Stundenzettel.pdf`)"><i class="bi bi-table me-1"></i>Stundenzettel</button>
                </div>
                <div class="lifecycle-buttons">
                  <button v-if="!workflowByOrder.get(order.orderId)?.approvalId" type="button" class="btn btn-primary btn-sm" :disabled="!canApprove || !approvalReady(order) || !!busyAction" @click="approveOrder(order)"><i class="bi bi-check2-square me-1"></i>Freigeben</button>
                  <button v-else-if="!workflowByOrder.get(order.orderId)?.latestRevisionId" type="button" class="btn btn-primary btn-sm" :disabled="!canApprove || !hasEInvoice || !documentForm.templateFileId || !!busyAction" @click="generateDocuments(order)"><i class="bi bi-file-earmark-plus me-1"></i>Dokumente erzeugen</button>
                  <button v-else-if="workflowByOrder.get(order.orderId)?.invoiceStatus === 'Draft'" type="button" class="btn btn-primary btn-sm" :disabled="!canApprove || !!busyAction" @click="finalizeOrder(order)"><i class="bi bi-lock me-1"></i>Finalisieren</button>
                  <button v-else-if="workflowByOrder.get(order.orderId)?.invoiceStatus === 'Finalized'" type="button" class="btn btn-success btn-sm" :disabled="!canDispatch || !!busyAction" @click="markSent(order)"><i class="bi bi-send me-1"></i>Als versendet markieren</button>
                  <button v-if="canApprove && ['Draft', 'Finalized', 'Sent'].includes(workflowByOrder.get(order.orderId)?.invoiceStatus)" type="button" class="btn btn-outline-danger btn-sm" :disabled="!!busyAction" @click="openCancellation(order)"><i class="bi bi-x-octagon me-1"></i>Stornieren</button>
                </div>
              </footer>
            </article>
            <p v-if="!runOrders.length" class="empty-row">Der Snapshot enthält keine abrechenbaren Aufträge.</p>
          </section>
        </template>
        <p v-else class="run-empty">Monat und Quellsystem auswählen, um einen unveränderlichen Billing Run zu erzeugen.</p>
      </template>
    </template>

    <div v-if="cancellationTarget" class="dialog-backdrop" @click.self="cancellationTarget = null">
      <section class="operation-dialog" role="dialog" aria-modal="true" aria-labelledby="cancel-heading">
        <header><h2 id="cancel-heading">Rechnung stornieren</h2><button type="button" class="btn-close" aria-label="Schließen" @click="cancellationTarget = null"></button></header>
        <p>Auftrag <strong>{{ cancellationTarget.orderNumber }}</strong></p>
        <form @submit.prevent="cancelOrder">
          <label for="cancellationReason" class="form-label">Stornogrund</label><input id="cancellationReason" v-model.trim="cancellationReason" class="form-control form-control-sm mb-3" maxlength="500" required />
          <label for="cancellationInvoiceId" class="form-label">Stornorechnungs-ID, falls vorhanden</label><input id="cancellationInvoiceId" v-model.trim="cancellationInvoiceId" class="form-control form-control-sm mb-3" placeholder="UUID" />
          <div class="dialog-actions"><button type="button" class="btn btn-outline-secondary btn-sm" @click="cancellationTarget = null">Abbrechen</button><button type="submit" class="btn btn-danger btn-sm" :disabled="!cancellationReason || !!busyAction"><i class="bi bi-x-octagon me-1"></i>Storno bestätigen</button></div>
        </form>
      </section>
    </div>
  </div>
</template>

<style scoped>
.billing-page { color: #20252b; margin: 0 auto; max-width: 1600px; padding: 24px clamp(16px, 3vw, 40px) 48px; }
.billing-header, .section-heading, .workflow-item header, .workflow-actions, .dialog-actions, .operation-dialog header { align-items: center; display: flex; justify-content: space-between; }
.billing-header { margin-bottom: 18px; }
.billing-header h1 { font-size: 1.5rem; font-weight: 650; margin: 0; }
.billing-header p { color: #68717a; margin: 3px 0 0; }
.icon-command { height: 34px; width: 34px; }
.billing-tabs { border-bottom: 1px solid #cbd2d8; display: flex; gap: 4px; margin-bottom: 20px; }
.billing-tabs button { background: transparent; border: 0; border-bottom: 3px solid transparent; color: #56616b; font-weight: 600; min-height: 42px; padding: 8px 14px; }
.billing-tabs button.active { border-color: #16697a; color: #0e5260; }
.billing-tabs i { margin-right: 7px; }
.billing-loading, .run-empty { align-items: center; background: #f1f4f6; border-block: 1px solid #d9dee3; display: flex; justify-content: center; min-height: 150px; }
.section-heading { border-bottom: 1px solid #cbd2d8; min-height: 42px; }
.section-heading h2, .operation-dialog h2 { font-size: 1rem; font-weight: 650; margin: 0; }
.section-heading > span { background: #e7edf0; border-radius: 4px; font-size: .78rem; font-weight: 700; min-width: 28px; padding: 3px 7px; text-align: center; }
.work-form, .preview-form, .document-grid { background: #f7f8f9; border-bottom: 1px solid #d9dee3; display: grid; gap: 12px; padding: 14px 16px; }
.work-form label, .preview-form label, .document-grid label { color: #56616b; font-size: .76rem; font-weight: 600; }
.work-form input, .work-form select, .preview-form input, .document-grid input, .document-grid select { margin-top: 4px; }
.order-form { grid-template-columns: repeat(5, minmax(120px, 1fr)); }
.assignment-form { grid-template-columns: repeat(4, minmax(150px, 1fr)) auto; }
.billing-table { font-size: .84rem; margin: 0; }
.billing-table th { background: #f3f5f6; color: #4b555e; font-size: .72rem; font-weight: 650; text-transform: uppercase; }
.billing-table td { border-color: #e3e7ea; padding-block: 9px; }
.empty-row { color: #68717a; padding: 30px 8px !important; text-align: center; }
.run-toolbar { margin-bottom: 18px; }
.preview-form { align-items: end; grid-template-columns: minmax(140px, .7fr) minmax(180px, 1fr) minmax(180px, 1fr) auto; }
.lookup-form { align-items: center; border-bottom: 1px solid #d9dee3; display: grid; gap: 12px; grid-template-columns: auto minmax(220px, 520px); padding: 10px 16px; }
.lookup-form label { color: #68717a; font-size: .78rem; }
.run-summary { border-block: 1px solid #cbd2d8; display: grid; grid-template-columns: 2fr 1fr 1fr .7fr; margin-bottom: 22px; }
.run-summary div { min-width: 0; padding: 11px 16px; }
.run-summary div + div { border-left: 1px solid #d9dee3; }
.run-summary span { color: #68717a; display: block; font-size: .7rem; text-transform: uppercase; }
.run-summary strong { display: block; font-size: .82rem; margin-top: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.document-settings { margin-bottom: 24px; }
.document-grid { grid-template-columns: repeat(6, minmax(105px, 1fr)); }
.document-grid .payment-terms { grid-column: span 3; }
.workflow-title { gap: 16px; }
.dispatch-tools { align-items: center; display: flex; flex-wrap: wrap; gap: 10px; }
.dispatch-tools .form-check { font-size: .8rem; margin: 0 4px 0 0; }
.workflow-item { border: 1px solid #cbd2d8; border-radius: 6px; margin-top: 14px; overflow: hidden; }
.workflow-item > header { background: #eef3f4; gap: 16px; padding: 12px 14px; }
.workflow-item h3 { font-size: 1rem; font-weight: 700; margin: 0; }
.workflow-item header div > span:not(.badge) { color: #68717a; display: block; font-size: .78rem; margin-top: 2px; }
.status-badges { align-items: center; display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
.calculation-grid { display: grid; grid-template-columns: repeat(7, minmax(92px, 1fr)); margin: 0; }
.calculation-grid div { border-bottom: 1px solid #e3e7ea; padding: 11px 12px; }
.calculation-grid div + div { border-left: 1px solid #e3e7ea; }
.calculation-grid dt { color: #68717a; font-size: .68rem; font-weight: 600; text-transform: uppercase; }
.calculation-grid dd { font-size: .88rem; font-weight: 650; margin: 3px 0 0; }
.calculation-grid .net-total { background: #edf7f1; }
.acceptance-strip { background: #fff8e4; border-bottom: 1px solid #ead9a7; display: flex; flex-wrap: wrap; gap: 20px; padding: 10px 14px; }
.acceptance-strip .form-check { font-size: .82rem; margin: 0; }
.feedback-error { background: #fff0f0; border-bottom: 1px solid #ecc7c7; color: #7d2424; display: flex; flex-wrap: wrap; font-size: .8rem; gap: 12px; padding: 9px 14px; }
.workflow-actions { gap: 12px; min-height: 52px; padding: 8px 12px; }
.document-links, .lifecycle-buttons { align-items: center; display: flex; flex-wrap: wrap; gap: 6px; }
.document-links .btn { padding-inline: 5px; }
.dialog-backdrop { align-items: center; background: rgba(22, 27, 32, .55); display: flex; inset: 0; justify-content: center; padding: 16px; position: fixed; z-index: 1060; }
.operation-dialog { background: #fff; border-radius: 6px; box-shadow: 0 18px 45px rgba(0, 0, 0, .25); max-width: 520px; padding: 20px; width: 100%; }
.operation-dialog header { border-bottom: 1px solid #d9dee3; margin-bottom: 16px; padding-bottom: 12px; }
.dialog-actions { border-top: 1px solid #d9dee3; gap: 8px; padding-top: 14px; }
@media (max-width: 1200px) {
  .order-form { grid-template-columns: repeat(3, minmax(140px, 1fr)); }
  .document-grid { grid-template-columns: repeat(3, minmax(140px, 1fr)); }
  .calculation-grid { grid-template-columns: repeat(4, minmax(100px, 1fr)); }
}
@media (max-width: 820px) {
  .assignment-form, .preview-form { grid-template-columns: 1fr 1fr; }
  .run-summary { grid-template-columns: 1fr 1fr; }
  .run-summary div:nth-child(3) { border-left: 0; border-top: 1px solid #d9dee3; }
  .run-summary div:nth-child(4) { border-top: 1px solid #d9dee3; }
  .workflow-title, .workflow-item header, .workflow-actions { align-items: flex-start; flex-direction: column; }
  .status-badges { justify-content: flex-start; }
}
@media (max-width: 580px) {
  .billing-page { padding-inline: 12px; }
  .billing-tabs { overflow-x: auto; }
  .billing-tabs button { flex: 0 0 auto; }
  .order-form, .assignment-form, .preview-form, .document-grid, .calculation-grid { grid-template-columns: 1fr; }
  .document-grid .payment-terms { grid-column: auto; }
  .lookup-form { align-items: stretch; grid-template-columns: 1fr; }
  .calculation-grid div + div { border-left: 0; }
}
</style>
